import { useQuery } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import { useTRPC } from "../lib/api";
import { useMe } from "./session";

/** The active participation (null while loading / not joined). */
export function useActive() {
  const me = useMe();
  return {
    me,
    active: me.data?.active ?? null,
    user: me.data?.user ?? null,
    isLoading: me.isLoading,
  };
}

/** quest.list with the brief's poll (pull-to-refresh calls refetch). */
export function useQuestList(eventId: string | undefined) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.quest.list.queryOptions({ eventId: eventId ?? "" }),
    enabled: Boolean(eventId),
    refetchInterval: 15_000,
  });
}

const PASSKEY_PROMPTED_KEY = "ec2.passkeyPrompted";

function subscribeStorage(cb: () => void) {
  window.addEventListener("storage", cb);
  return () => window.removeEventListener("storage", cb);
}

/**
 * Rung-1 moment (spec §1.5 / master plan 4.7): offer the passkey save prompt
 * once, right after the first reward, for anonymous users only.
 */
export function usePasskeyPrompt() {
  const { user } = useActive();
  const prompted = useSyncExternalStore(
    subscribeStorage,
    () => {
      try {
        return localStorage.getItem(PASSKEY_PROMPTED_KEY) === "1";
      } catch {
        return true;
      }
    },
    () => true,
  );
  const markPrompted = useCallback(() => {
    try {
      localStorage.setItem(PASSKEY_PROMPTED_KEY, "1");
      window.dispatchEvent(new StorageEvent("storage", { key: PASSKEY_PROMPTED_KEY }));
    } catch {
      /* ignore */
    }
  }, []);
  return {
    // Not when a passkey already exists — e.g. saved from the profile first, or
    // on another browser of this device (the local "prompted" flag can't know).
    shouldPrompt: !prompted && user?.kind === "anonymous" && user.hasPasskey !== true,
    markPrompted,
  };
}
